import { useState } from 'react';
import { Edit, CheckCircle, XCircle, Clock, Calendar, Package, DollarSign, User, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PurchaseOrderLine, purchasingAPI } from '../../lib/api';
import { ChangelogEntry } from '../common/Changelog';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import { toast } from 'sonner';
import ChangeRequestCard from './ChangeRequestCard';
import { groupChangeRequests, useChangeRequestAttachments } from './changeRequestGrouping';

interface ChangeRequestsTabProps {
  line: PurchaseOrderLine;
  changelogs: ChangelogEntry[];
  userType: 'customer' | 'supplier';
  onAccept: () => void;
  onDecline: () => void;
  onUpdate?: () => void;
}

const CHANGE_FIELDS = new Set([
  'supplier_promise_date',
  'quantity',
  'unit_price',
]);

const CHANGE_STATUS_VALUES = new Set([
  'change_proposed_supplier',
  'change_proposed_customer',
  'accepted',
  'change_declined',
]);

const FIELD_LABELS: Record<string, string> = {
  supplier_promise_date: 'Supplier Promise Date',
  quantity: 'Quantity',
  unit_price: 'Unit Price',
  acknowledge_status: 'Status',
};

const FIELD_ICONS: Record<string, LucideIcon> = {
  supplier_promise_date: Calendar,
  quantity: Package,
  unit_price: DollarSign,
  acknowledge_status: Edit,
};

const STATUS_DISPLAY: Record<string, string> = {
  change_proposed_supplier: 'Proposed by Supplier',
  change_proposed_customer: 'Proposed by Customer',
  accepted: 'Accepted',
  change_declined: 'Declined',
};

function getStatusBadge(status: string) {
  const config: Record<string, { label: string; className: string; icon: LucideIcon }> = {
    open: { label: 'Open - Awaiting Decision', className: 'bg-orange-100 text-orange-800 border-orange-200', icon: Clock },
    // CR-4863 round 2: History rows describing the original proposal event
    // use this neutral past-tense badge so they do not keep claiming the
    // request is "Awaiting Decision" after it has been accepted/rejected.
    // The live "Open - Awaiting Decision" badge is reserved for the Active
    // Change Request card, which auto-hides when the line is resolved.
    proposed: { label: 'Proposed', className: 'bg-orange-100 text-orange-800 border-orange-200', icon: Edit },
    accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    // CR-4863: name the status "Rejected" rather than the legacy "Declined"
    // so the audit-trail label matches the action button users clicked
    // ("Reject"). Spec calls it out explicitly.
    declined: { label: 'Rejected', className: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
    // CR-4864: surface "Cancelled" alongside the other terminal states so
    // proposer-cancelled rows in Change History don't fall through to the
    // open / "no badge" paths.
    cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-700 border-gray-200', icon: XCircle },
  };
  const c = config[status] || config['open'];
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${c.className}`}>
      <Icon className="h-3 w-3" />
      {c.label}
    </span>
  );
}

function isChangeRelatedStatusEntry(entry: ChangelogEntry): boolean {
  if (entry.field_changed !== 'acknowledge_status') return false;
  const oldVal = entry.old_value || '';
  const newVal = entry.new_value || '';
  return CHANGE_STATUS_VALUES.has(oldVal) || CHANGE_STATUS_VALUES.has(newVal);
}

type ChangeField = 'date' | 'quantity' | 'price';

export default function ChangeRequestsTab({ line, changelogs, userType, onAccept, onDecline, onUpdate }: ChangeRequestsTabProps) {
  const { formatDate: contextFormatDate, formatDateTime: contextFormatDateTime } = useFormatters();
  const { hasPurchasingWriteAccess } = useUserPermissions();

  const [fieldActionLoading, setFieldActionLoading] = useState<string | null>(null);
  const [rejectField, setRejectField] = useState<ChangeField | 'all' | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // CR-4688: Show the active change request card when proposals are pending.
  // Primary signal: acknowledge_status is change_proposed_*. Fallback: any
  // proposed value fields are still populated (handles stale data where the
  // old decline path didn't clear them).
  const hasProposalStatus = line.acknowledge_status === 'change_proposed_supplier' || line.acknowledge_status === 'change_proposed_customer';
  const hasAnyProposedValue = !!(line.supplier_proposed_date || line.supplier_proposed_unit_price || line.supplier_proposed_qty
    || (line.supplier_promise_date && line.delivery_date && line.supplier_promise_date !== line.delivery_date));
  // CR-4688 round 3 rework: terminal acknowledge_status (accepted /
  // change_declined) is the source of truth — once the workflow service
  // has set it, no proposal is open regardless of any date-mismatch
  // residue. Without this guard, accepting the final per-field change
  // left "Active Change Request" rendered indefinitely because
  // supplier_promise_date stays at the accepted value while delivery_date
  // keeps the original required date, so the "promise_date !== delivery_date"
  // fallback in hasAnyProposedValue stayed truthy forever.
  const isResolved = line.acknowledge_status === 'accepted' || line.acknowledge_status === 'change_declined';
  // CR-4882: Defend against stuck legacy data where acknowledge_status is
  // change_proposed_* but every supplier_proposed_* field has been cleared
  // (supplier cancelled before the round-3 backend self-heal landed).
  // Without this guard the panel renders an empty Active Change Request
  // card with phantom Accept/Reject buttons. The data migration heals
  // existing rows; this is the read-side belt to the suspenders.
  const isStuckProposalStatus = hasProposalStatus && !hasAnyProposedValue;
  const hasOpenChange = !isResolved && !isStuckProposalStatus && (hasProposalStatus || hasAnyProposedValue);

  const canActOnChange = (): boolean => {
    const isCustomer = userType === 'customer';
    const isSupplier = userType === 'supplier';

    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    if (isCustomer && line.acknowledge_status === 'change_proposed_supplier') return true;
    if (isSupplier && line.acknowledge_status === 'change_proposed_customer') return true;
    // CR-4688: Allow acting on stale proposals where status moved past
    // change_proposed but proposed values weren't cleared.
    if (hasAnyProposedValue && (isCustomer || isSupplier)) return true;
    return false;
  };

  // CR-4688 rework: the date row's "active" flag must come ONLY from
  // supplier_proposed_date. Once the backend accepts a date change, it
  // nulls supplier_proposed_date and keeps supplier_promise_date at the
  // accepted value — falling back to supplier_promise_date here made the
  // row stay "Open" even though the audit trail showed it as Accepted.
  // Price/qty already use the proposed field directly (no fallback) so
  // they don't have this bug.
  const proposedDateTo = line.supplier_proposed_date || '';
  const proposedDateFrom = line.original_promise_date || line.delivery_date || '';
  const hasProposedDate = !!(proposedDateTo && proposedDateFrom && proposedDateTo !== proposedDateFrom);
  const hasProposedPrice = !!line.supplier_proposed_unit_price;
  const hasProposedQty = !!line.supplier_proposed_qty;
  const activeChangeFields = [hasProposedDate && 'date' as const, hasProposedPrice && 'price' as const, hasProposedQty && 'quantity' as const].filter(Boolean) as ChangeField[];
  const proposalCount = activeChangeFields.length;

  const changeEntries = changelogs.filter(
    (entry) => CHANGE_FIELDS.has(entry.field_changed) || isChangeRelatedStatusEntry(entry)
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDateTime(dateString);
  };

  const getProposedBy = () => {
    if (line.acknowledge_status === 'change_proposed_supplier') return 'Supplier';
    if (line.acknowledge_status === 'change_proposed_customer') return 'Customer';
    return 'Unknown';
  };

  const getFieldLabel = (field: string) => FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const getHistoryStatusBadge = (entry: ChangelogEntry) => {
    // Acknowledge-status field changes carry the status verbatim in new_value.
    if (entry.field_changed === 'acknowledge_status') {
      const newVal = entry.new_value;
      if (newVal === 'accepted') return getStatusBadge('accepted');
      if (newVal === 'change_declined') return getStatusBadge('declined');
      // CR-4863 round 2: proposal history rows render as "Proposed" (past
      // tense). "Open - Awaiting Decision" only makes sense for the live
      // Active Change Request card — once a history row is written, the
      // event it describes has already happened.
      if (newVal === 'change_proposed_supplier' || newVal === 'change_proposed_customer') return getStatusBadge('proposed');
    }
    // CR-4864: per-field rows (Promise Date, Quantity, Unit Price) don't
    // have a status field of their own — derive their outcome from the
    // event_type and reason text the workflow service writes, so users can
    // tell at a glance whether each historical row was accepted, rejected,
    // cancelled, or still open. Keeps the badge vocabulary consistent with
    // the Active Change Request panel above.
    const reason = (entry.reason || '').toLowerCase();
    if (entry.event_type === 'change_accepted' || /\baccepted\b/.test(reason)) {
      return getStatusBadge('accepted');
    }
    if (/\b(rejected|declined)\b/.test(reason)) {
      return getStatusBadge('declined');
    }
    if (/\bcancelled\b/.test(reason)) {
      return getStatusBadge('cancelled');
    }
    if (entry.event_type === 'change_request' || /\bproposed\b/.test(reason)) {
      return getStatusBadge('proposed');
    }
    return null;
  };

  const handleAcceptField = async (field: ChangeField) => {
    setFieldActionLoading(field);
    try {
      const lineId = (line as unknown as Record<string, unknown>)['id'] as number;
      if (field === 'date') {
        await purchasingAPI.acceptDeliveryDate(lineId, {});
      } else if (field === 'quantity') {
        await purchasingAPI.acceptQuantityChange(lineId, {});
      } else if (field === 'price') {
        await purchasingAPI.acceptPriceChange(lineId, {});
      }
      toast.success(`${field === 'date' ? 'Promise date' : field === 'price' ? 'Unit price' : 'Quantity'} change accepted`);
      onUpdate?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to accept change');
    } finally {
      setFieldActionLoading(null);
    }
  };

  const handleRejectField = async () => {
    if (!rejectField || !rejectReason.trim()) return;
    setFieldActionLoading(rejectField);
    const lineId = (line as unknown as Record<string, unknown>)['id'] as number;
    const targets: ChangeField[] = rejectField === 'all' ? activeChangeFields : [rejectField];
    try {
      // CR-4706 round 2: sequential, same rationale as handleAcceptAll —
      // parallel rejects race on the same PO line row and lose clears.
      let failed = 0;
      for (const f of targets) {
        try {
          await purchasingAPI.rejectFieldChange(lineId, { field: f, reason: rejectReason });
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) toast.success(targets.length > 1 ? 'All changes rejected' : `${targets[0] === 'date' ? 'Promise date' : targets[0] === 'price' ? 'Unit price' : 'Quantity'} change rejected`);
      else toast.error(`${failed} change${failed > 1 ? 's' : ''} failed to reject`);
      setRejectField(null);
      setRejectReason('');
      onUpdate?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to reject change');
    } finally {
      setFieldActionLoading(null);
    }
  };

  const handleAcceptAll = async () => {
    setFieldActionLoading('all');
    const lineId = (line as unknown as Record<string, unknown>)['id'] as number;
    try {
      // CR-4706 round 2: process sequentially, NOT in parallel. Each accept
      // endpoint loads the PO line, clears one proposed_* field, and saves.
      // When these ran via Promise.allSettled the three calls raced on the
      // same row — each worker read a stale snapshot with all three
      // proposed_* fields still populated, cleared its own, and the last
      // writer clobbered the earlier clears. The UI then refreshed and
      // showed Quantity + Unit Price still "Active" even though all three
      // audit rows had been written. Sequential awaits avoid the race and
      // keep the per-field action separable (so a failure on one doesn't
      // abort the rest — we surface a partial-failure toast).
      let failed = 0;
      for (const f of activeChangeFields) {
        try {
          if (f === 'date') await purchasingAPI.acceptDeliveryDate(lineId, {});
          else if (f === 'price') await purchasingAPI.acceptPriceChange(lineId, {});
          else await purchasingAPI.acceptQuantityChange(lineId, {});
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) toast.success('All changes accepted');
      else toast.error(`${failed} change${failed > 1 ? 's' : ''} failed to accept`);
      onUpdate?.();
    } finally {
      setFieldActionLoading(null);
    }
  };

  // CR-4680: Supplier cancels their own proposed change
  const handleCancelField = async (field: ChangeField) => {
    setFieldActionLoading(field);
    try {
      const lineId = (line as unknown as Record<string, unknown>)['id'] as number;
      await purchasingAPI.cancelFieldChange(lineId, { field });
      toast.success(`${field === 'date' ? 'Promise date' : field === 'price' ? 'Unit price' : 'Quantity'} change cancelled`);
      onUpdate?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to cancel change');
    } finally {
      setFieldActionLoading(null);
    }
  };

  const hasNoChanges = !hasOpenChange && changeEntries.length === 0;

  // CR-4862: Reject reason form rendered inline under the specific field
  // being rejected (or at the bottom of the list for "Reject All"). Same
  // markup either way — the only thing that changes is the heading and
  // which targets the confirm button hits via handleRejectField.
  const renderRejectForm = (forField: ChangeField | 'all') => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
      <p className="text-sm font-medium text-red-800">
        {forField === 'all' ? 'Reject all changes' : `Reject ${forField === 'date' ? 'Promise Date' : forField === 'price' ? 'Unit Price' : 'Quantity'} change`}
      </p>
      <input
        type="text"
        placeholder="Reason for rejection (required)"
        value={rejectReason}
        onChange={(e) => setRejectReason(e.target.value)}
        className="w-full px-3 py-2 border rounded text-sm"
        autoFocus
      />
      <div className="flex gap-2">
        <button
          onClick={handleRejectField}
          disabled={!rejectReason.trim() || !!fieldActionLoading}
          className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
        >
          {fieldActionLoading === forField ? <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> : null}
          Confirm Reject
        </button>
        <button
          onClick={() => { setRejectField(null); setRejectReason(''); }}
          className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // CR-4706: Proposer is derived strictly from acknowledge_status. A
  // hasAnyProposedValue fallback misidentifies the counterparty as proposer.
  const isProposer = (
    (userType === 'supplier' && line.acknowledge_status === 'change_proposed_supplier') ||
    (userType === 'customer' && line.acknowledge_status === 'change_proposed_customer')
  );
  const isReviewer = canActOnChange() && !isProposer;

  // CR-4688 round 3 rework: always use per-field actions when a specific
  // field is in play. The previous gate (proposalCount > 1) sent single-
  // field accepts/rejects through the parent's bulk AcceptDateModal /
  // DeclineDateModal — both hardcoded to "promise date" wording and to
  // purchasingAPI.acceptDeliveryDate. So accepting the FINAL Quantity or
  // Unit Price change said "Promise date accepted successfully" and hit
  // the date endpoint instead of accept_quantity_change / accept_price_change,
  // which left the proposal unresolved on the backend and the row open in
  // the queue. The per-field handlers below already route to the correct
  // endpoint and surface field-aware toast wording, so use them whenever
  // we know which field is in play.
  const showPerFieldActions = isReviewer || isProposer;

  // CR-4865: split the changelog into Delivery-Appeal-style grouped cards
  // (rows with a change_request_ref) and legacy ungrouped rows. The
  // legacy Change History block further down handles the orphan entries.
  const { groups, orphans } = groupChangeRequests(changeEntries);
  const poLineId = (line as unknown as { id?: number }).id || 0;
  const { attachmentsByRef, refresh: refreshAttachments } = useChangeRequestAttachments(poLineId);
  const canUploadSide = canActOnChange() || isProposer;

  // Bulk Accept/Decline modals are date-specific and remain only as the
  // fallback for stale change_proposed_* statuses that have no
  // supplier_proposed_* values populated (proposalCount === 0).
  const showBulkActions = isReviewer && proposalCount === 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Edit className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-lg">Change Requests</h3>
        {changeEntries.length > 0 && (
          <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-sm">
            {changeEntries.length}
          </span>
        )}
      </div>

      {/* Empty State */}
      {hasNoChanges && (
        <div className="text-center py-12 border rounded-lg bg-muted/20">
          <Edit className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground font-medium">No changes found for this PO line</p>
        </div>
      )}

      {/* Open Change Request */}
      {hasOpenChange && (
        <div className="border-2 border-orange-300 rounded-lg bg-orange-50/50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-orange-100/60 border-b border-orange-200">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              <span className="font-semibold text-orange-800">Active Change Request</span>
            </div>
            {getStatusBadge('open')}
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-3.5 w-3.5" />
              <span>
                {getProposedBy()}
                {line.acknowledged_by_details?.full_name && ` (${line.acknowledged_by_details.full_name})`}
                {line.acknowledge_date && ` on ${formatDateTime(line.acknowledge_date)}`}
              </span>
            </div>

            {/* Proposed Values — CR-4688: per-field actions when multiple proposals.
                CR-4862: the reject reason form (renderRejectForm below) is
                rendered inline directly beneath the field being rejected
                rather than at the bottom of the whole list, so the visual
                association between the action and the input is unambiguous.
                The "Reject All" form keeps its bottom-of-list position
                because it spans every field. */}
            <div className="space-y-2">
              {hasProposedDate && (<>
                <div className="flex items-center gap-3 bg-white rounded-lg p-3 border">
                  <Calendar className="h-4 w-4 text-orange-600 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Promise Date</div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">{formatDate(proposedDateFrom)}</span>
                      <span className="mx-2 text-muted-foreground">&rarr;</span>
                      <span className="font-semibold text-orange-700">{formatDate(proposedDateTo)}</span>
                    </div>
                  </div>
                  {showPerFieldActions && (
                    <div className="flex items-center gap-1">
                      {isProposer ? (
                        <button onClick={() => handleCancelField('date')} disabled={!!fieldActionLoading}
                          className="flex items-center gap-1 px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-xs font-medium disabled:opacity-50">
                          {fieldActionLoading === 'date' ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                          Cancel
                        </button>
                      ) : (<>
                        <button onClick={() => handleAcceptField('date')} disabled={!!fieldActionLoading}
                          className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-medium disabled:opacity-50">
                          {fieldActionLoading === 'date' ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                          Accept
                        </button>
                        <button onClick={() => setRejectField('date')} disabled={!!fieldActionLoading}
                          className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium disabled:opacity-50">
                          <XCircle className="h-3 w-3" /> Reject
                        </button>
                      </>)}
                    </div>
                  )}
                </div>
                {rejectField === 'date' && renderRejectForm('date')}
              </>)}
              {hasProposedPrice && (<>
                <div className="flex items-center gap-3 bg-white rounded-lg p-3 border">
                  <DollarSign className="h-4 w-4 text-orange-600 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Unit Price</div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">{line.unit_price}</span>
                      <span className="mx-2 text-muted-foreground">&rarr;</span>
                      <span className="font-semibold text-orange-700">{line.supplier_proposed_unit_price}</span>
                    </div>
                  </div>
                  {showPerFieldActions && (
                    <div className="flex items-center gap-1">
                      {isProposer ? (
                        <button onClick={() => handleCancelField('price')} disabled={!!fieldActionLoading}
                          className="flex items-center gap-1 px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-xs font-medium disabled:opacity-50">
                          {fieldActionLoading === 'price' ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                          Cancel
                        </button>
                      ) : (<>
                        <button onClick={() => handleAcceptField('price')} disabled={!!fieldActionLoading}
                          className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-medium disabled:opacity-50">
                          {fieldActionLoading === 'price' ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                          Accept
                        </button>
                        <button onClick={() => setRejectField('price')} disabled={!!fieldActionLoading}
                          className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium disabled:opacity-50">
                          <XCircle className="h-3 w-3" /> Reject
                        </button>
                      </>)}
                    </div>
                  )}
                </div>
                {rejectField === 'price' && renderRejectForm('price')}
              </>)}
              {hasProposedQty && (<>
                <div className="flex items-center gap-3 bg-white rounded-lg p-3 border">
                  <Package className="h-4 w-4 text-orange-600 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Quantity</div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">{line.quantity}</span>
                      <span className="mx-2 text-muted-foreground">&rarr;</span>
                      <span className="font-semibold text-orange-700">{line.supplier_proposed_qty}</span>
                    </div>
                  </div>
                  {showPerFieldActions && (
                    <div className="flex items-center gap-1">
                      {isProposer ? (
                        <button onClick={() => handleCancelField('quantity')} disabled={!!fieldActionLoading}
                          className="flex items-center gap-1 px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-xs font-medium disabled:opacity-50">
                          {fieldActionLoading === 'quantity' ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                          Cancel
                        </button>
                      ) : (<>
                        <button onClick={() => handleAcceptField('quantity')} disabled={!!fieldActionLoading}
                          className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-medium disabled:opacity-50">
                          {fieldActionLoading === 'quantity' ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                          Accept
                        </button>
                        <button onClick={() => setRejectField('quantity')} disabled={!!fieldActionLoading}
                          className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium disabled:opacity-50">
                          <XCircle className="h-3 w-3" /> Reject
                        </button>
                      </>)}
                    </div>
                  )}
                </div>
                {rejectField === 'quantity' && renderRejectForm('quantity')}
              </>)}
              {!hasProposedDate && !hasProposedPrice && !hasProposedQty && (
                <div className="bg-white rounded-lg p-3 border text-sm text-muted-foreground">
                  A change has been proposed. Review the details in the Detail tab.
                </div>
              )}
            </div>

            {/* CR-4862: Reject reason form for "Reject All" stays at the
                bottom because it spans every field. Per-field reject forms
                are now rendered inline beneath their respective rows above. */}
            {rejectField === 'all' && renderRejectForm('all')}

            {isReviewer && proposalCount > 1 && (
              <div className="flex items-center gap-3 pt-2 border-t border-orange-200">
                <button onClick={handleAcceptAll} disabled={!!fieldActionLoading} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50">
                  {fieldActionLoading === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />} Accept All
                </button>
                <button onClick={() => setRejectField('all')} disabled={!!fieldActionLoading} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50">
                  <XCircle className="h-4 w-4" /> Reject All
                </button>
              </div>
            )}

            {/* Bulk Accept / Reject — shown when only one proposal (backward compat) */}
            {showBulkActions && (
              <div className="flex items-center gap-3 pt-2 border-t border-orange-200">
                <button
                  onClick={onAccept}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  <CheckCircle className="h-4 w-4" />
                  Accept
                </button>
                <button
                  onClick={onDecline}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  <XCircle className="h-4 w-4" />
                  Reject
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CR-4865: Grouped change-request cards (Delivery-Appeal-style).
          Rows carrying change_request_ref group into one card; older rows
          without a ref fall through to the legacy Change History list below. */}
      {groups.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Change Requests ({groups.length})</h4>
          {groups.map(group => (
            <ChangeRequestCard
              key={group.ref}
              group={{ ...group, poLineId: poLineId }}
              attachments={attachmentsByRef.get(group.ref) || []}
              canUploadSubmission={canUploadSide}
              canUploadDecision={canUploadSide}
              onAttachmentsChanged={refreshAttachments}
              formatDateTime={formatDateTime}
            />
          ))}
        </div>
      )}

      {/* Legacy Change History — renders rows that don't carry a
          change_request_ref (pre-CR-4865 data). Once all historical rows
          have been resolved, this section stays empty forever. */}
      {orphans.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Change History</h4>
          {orphans.map((entry, index) => {
            const FieldIcon = FIELD_ICONS[entry.field_changed] || Edit;
            const statusBadge = getHistoryStatusBadge(entry);
            return (
              <div key={entry.id || index} className="border rounded-lg p-3 bg-white shadow-sm">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FieldIcon className="h-4 w-4 text-primary flex-shrink-0" />
                    <span className="font-medium text-sm">{getFieldLabel(entry.field_changed)}</span>
                    {statusBadge}
                  </div>
                  <div className="text-xs text-muted-foreground text-right">
                    {formatDateTime(entry.change_date)}
                  </div>
                </div>
                {entry.field_changed === 'acknowledge_status' ? (
                  <div className="text-sm">
                    <span className="text-muted-foreground">{STATUS_DISPLAY[entry.old_value || ''] || entry.old_value}</span>
                    <span className="mx-2 text-muted-foreground">&rarr;</span>
                    <span className="font-medium">{STATUS_DISPLAY[entry.new_value] || entry.new_value}</span>
                  </div>
                ) : (
                  <div className="text-sm">
                    <span className="text-muted-foreground">{entry.old_value || 'N/A'}</span>
                    <span className="mx-2 text-muted-foreground">&rarr;</span>
                    <span className="font-medium">{entry.new_value}</span>
                  </div>
                )}
                {entry.reason && (
                  <div className="text-xs text-muted-foreground mt-1">Reason: {entry.reason}</div>
                )}
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {entry.changed_by}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
